import { ReactElement, useEffect, useRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import BaseLayout from 'src/v3/components/base-layout'
import {
  fetchAssignments,
  fetchCourseProgressSummaries,
  fetchSections,
  selectAssignmentsStatus,
  selectSectionsStatus,
} from './self-paced.slice'
import {
  classIDChanged,
  courseIDChanged,
  progressResetted,
} from '../classroom/classroom.slice'

interface Props {
  children: ReactElement | ReactElement[]
}

export default ({ children }: Props): ReactElement => {
  let { classID } = useParams()
  let { courseID } = useParams()
  let location = useLocation()
  // SelfPacedLayout also wraps /classroom-v2/* and /classroom/* (see
  // App.tsx), so it never unmounts on a self-paced -> course player -> back
  // journey — a mount-only effect here only ever fires once per session.
  // This ref tracks whether the PREVIOUS location was inside self-paced, so
  // the progress refetch below fires on genuine re-entry into the self-paced
  // context, not on every mount (there is none) or on internal navigation
  // between self-paced's own pages.
  let wasInSelfPaced = useRef(false)

  let sectionsStatus = useAppSelector(selectSectionsStatus)
  let assignmentsStatus = useAppSelector(selectAssignmentsStatus)
  let dispatch = useAppDispatch()

  useEffect(() => {
    if (sectionsStatus === 'uninitialized') {
      dispatch(fetchSections())
    }
  }, [sectionsStatus])

  useEffect(() => {
    if (assignmentsStatus === 'uninitialized') {
      dispatch(fetchAssignments())
    }
  }, [assignmentsStatus])

  // Make sure classroom resets when classID param changes in any self-paced route
  useEffect(() => {
    dispatch(progressResetted())
    dispatch(courseIDChanged(courseID ?? ''))
    dispatch(classIDChanged(classID ?? ''))
  }, [classID])

  // Refires on every genuine landing in self-paced — a transition from a
  // non-self-paced path into one — rather than on mount, since this
  // component's mount doesn't correspond to that (see wasInSelfPaced above).
  // First-load skeletons are driven by courseProgressHasLoadedOnce in the
  // slice, not by gating this dispatch.
  useEffect(() => {
    let isInSelfPaced = location.pathname.startsWith('/self-paced')
    if (isInSelfPaced && !wasInSelfPaced.current) {
      dispatch(fetchCourseProgressSummaries())
    }
    // eslint-disable-next-line fp/no-mutation
    wasInSelfPaced.current = isInSelfPaced
  }, [location.pathname, dispatch])

  return <BaseLayout children={children} />
}
